import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, SearchCode } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const CASE_LABEL = {
  cancelled: 'cancelled',
  invalid_month_or_property: 'invalid month/property',
  unmapped_property_id: 'unmapped property id',
  no_statement_doc: 'no statement doc',
  reconciled: 'reconciled',
  not_reconciled: 'not reconciled',
  ref_matched_amount_mismatch: 'amount mismatch',
};

const CASE_VARIANT = {
  reconciled: 'outline',
  ref_matched_amount_mismatch: 'secondary',
  not_reconciled: 'secondary',
};

function CaseBadge({ verdict }) {
  if (!verdict) return <span className="text-muted-foreground">—</span>;
  const variant = CASE_VARIANT[verdict.case] || 'destructive';
  return (
    <div>
      <Badge variant={variant}>{CASE_LABEL[verdict.case] || verdict.case}</Badge>
      {!verdict.selected_for_bookings_tab && (
        <div className="text-muted-foreground mt-0.5">not shown on Bookings tab</div>
      )}
    </div>
  );
}

function DatasetEndpointCell({ doc }) {
  const de = doc.dataset_endpoint;
  if (!de || de.guest_total === null || de.guest_total === undefined) {
    return <span className="text-amber-600">—</span>;
  }
  const rawTotal = doc.total_price;
  const rawNum = rawTotal === null || rawTotal === undefined ? null : Number(rawTotal);
  const datasetNum = Number(de.guest_total);
  const diverges = rawNum !== null && Math.abs(rawNum - datasetNum) > 0.005;
  return (
    <div>
      <div className={diverges ? 'text-red-600 font-semibold' : ''}>
        {de.guest_total}
      </div>
      {de.fx_converted && (
        <div
          className="text-amber-600"
          title={`Converted ${de.currency_used} -> ${de.property_currency} before display. If ${de.currency_used} is a stale mislabel, this is exactly the out-of-window currency-freeze bug (see services/smoobu.py::_heal_out_of_window_currency).`}
        >
          FX: {de.currency_used}&rarr;{de.property_currency}
        </div>
      )}
      {diverges && (
        <div className="text-red-600" title="This is what the Bookings tab actually renders for this doc -- it does not match the raw total_price shown to the left, i.e. this row is NOT what the probe's own total_price column would suggest.">
          &ne; raw total_price
        </div>
      )}
    </div>
  );
}

function PairingBadge({ source, flag }) {
  const base = (() => {
    if (source === 'superseded_by') {
      return <Badge variant="outline">paired: superseded_by</Badge>;
    }
    if (source === 'live_matcher_not_yet_applied') {
      return (
        <Badge variant="destructive" title="The live dedup matcher would pair these, but run_dedup() has not applied it yet -- both docs may currently pass the Bookings tab filter.">
          paired: live matcher, not applied
        </Badge>
      );
    }
    return <Badge variant="outline">unpaired</Badge>;
  })();
  return (
    <div className="space-y-1">
      {base}
      {flag === 'live_booking_paired_to_cancelled_winner' && (
        <Badge
          variant="destructive"
          className="block w-fit"
          title="The Hostaway side of this pair is cancelled while the Smoobu side is live -- a cancelled doc must never hide or replace a live booking. run_dedup() rejects/self-heals this pairing; this flag catches any residual drift."
        >
          cancelled winner over live booking
        </Badge>
      )}
    </div>
  );
}

const BACKFILL_MAX_DISPLAY_ROWS = 50;

function CurrencyBackfillSection() {
  const [running, setRunning] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);

  const runPreview = async () => {
    setRunning(true);
    setError(null);
    setApplyResult(null);
    setConfirmApply(false);
    try {
      const { data } = await diagnosticsAPI.migrationBookingProbeCurrencyBackfillPreview();
      setPreview(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const runApply = async () => {
    setRunning(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.migrationBookingProbeCurrencyBackfillApply();
      setApplyResult(data);
      setConfirmApply(false);
      toast.success(
        `Currency backfill applied: ${data.updated} doc(s) relabeled. ` +
        `${data.remaining_mismatched_count} still mismatched after (expect 0).`
      );
      // Refresh the preview so the card reflects the post-apply cohort.
      const { data: freshPreview } = await diagnosticsAPI.migrationBookingProbeCurrencyBackfillPreview();
      setPreview(freshPreview);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border-t pt-4 space-y-3">
      <div>
        <p className="text-sm font-medium flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-blue-500" />
          Smoobu Currency Mislabel Backfill (portfolio-wide, DB-only)
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Smoobu is retired: no property will ever sync from it again, so the sync/webhook-path
          currency heal never runs for docs frozen before it existed. This derives the correct
          currency straight from data already on file (the property's own registered currency,
          same derivation the July-11 ingestion fix uses) and relabels currency ONLY -- never
          total_price, net_revenue, or any amount field. Covers every Smoobu property, not just
          ones migrated to Hostaway.
        </p>
      </div>

      <div className="flex gap-2">
        <Button onClick={runPreview} disabled={running} data-testid="currency-backfill-preview-btn">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Preview
        </Button>
        {!confirmApply ? (
          <Button
            variant="destructive"
            onClick={() => setConfirmApply(true)}
            disabled={running || !preview}
            data-testid="currency-backfill-apply-btn"
          >
            Apply
          </Button>
        ) : (
          <Button variant="destructive" onClick={runApply} disabled={running} data-testid="currency-backfill-confirm-apply-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
            Confirm Apply
          </Button>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
          Error: {error}
        </div>
      )}

      {preview && !error && (
        <div className="space-y-3" data-testid="currency-backfill-preview-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Mismatched docs</p>
              <p className="text-xl font-bold tabular-nums">{preview.mismatched_count}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Properties affected</p>
              <p className="text-xl font-bold tabular-nums">{preview.properties_affected}</p>
            </div>
            <div className="rounded-lg border p-3 col-span-2">
              <p className="text-xs text-muted-foreground">Display delta once applied</p>
              <p className="text-xl font-bold tabular-nums">
                {Object.entries(preview.display_delta_by_currency || {}).length === 0
                  ? '—'
                  : Object.entries(preview.display_delta_by_currency).map(([ccy, amt]) => (
                      <span key={ccy} className="mr-3">+{amt} {ccy}</span>
                    ))}
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">{preview.message}</p>

          {preview.by_property?.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium">Property</th>
                    <th className="text-right px-3 py-2 font-medium">Mismatched</th>
                    <th className="text-left px-3 py-2 font-medium">Sample (doc / stored -&gt; derived / total_price)</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.by_property.slice(0, BACKFILL_MAX_DISPLAY_ROWS).map((p) => (
                    <tr key={p.property_id} className="border-b last:border-0 align-top">
                      <td className="px-3 py-2">{p.property_name || p.property_id}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.mismatched_count}</td>
                      <td className="px-3 py-2 font-mono space-y-0.5">
                        {(p.sample || []).map((s) => (
                          <div key={s.smoobu_id}>
                            {s.smoobu_id}: {s.stored_currency}&rarr;{s.derived_currency} ({s.total_price})
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {applyResult && (
            <div className="flex items-center gap-2 text-sm">
              {applyResult.remaining_mismatched_count === 0 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-600 dark:text-green-400">
                    Applied: {applyResult.updated} relabeled, 0 remaining.
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-amber-600 dark:text-amber-400">
                    Applied: {applyResult.updated} relabeled, {applyResult.remaining_mismatched_count} still
                    mismatched -- investigate before assuming this is done.
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MigrationBookingProbePanel() {
  const [properties, setProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const [start, setStart] = useState('2026-06-01');
  const [end, setEnd] = useState('2026-07-01');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      setPropsLoading(true);
      try {
        const { data } = await diagnosticsAPI.migrationBookingProbePropertyOptions();
        const opts = data?.properties || [];
        setProperties(opts);
        const defaultName = data?.default_property;
        const defaultMatch = opts.find((p) => p.display_name === defaultName);
        if (defaultMatch) setPropertyName(defaultMatch.display_name);
      } catch (err) {
        console.error('Failed to load migration booking probe property options:', err);
      } finally {
        setPropsLoading(false);
      }
    })();
  }, []);

  const canRun = propertyName.trim() && start.trim() && end.trim();

  const run = async () => {
    if (!canRun) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.migrationBookingProbe({
        property: propertyName.trim(),
        start: start.trim(),
        end: end.trim(),
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const reservations = (r && r.reservations) || [];
  // Flattened once here (not via nested .map() calls inside JSX) so
  // <tbody> receives a single flat list of <tr> -- a map-inside-map
  // directly in JSX renders each row wrapped in an extra element, which
  // is invalid immediately under <tbody>.
  const rows = reservations.flatMap((res, ri) =>
    res.docs.map((doc, di) => ({ res, doc, ri, di }))
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <SearchCode className="h-5 w-5 text-blue-600" />
          Migration Booking Probe (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          For one property, shows its Hostaway migration cutover entry, every Smoobu/Hostaway
          reservation doc in the window as a pair row (source, total_price, superseded_by state,
          last synced), which doc the real Bookings tab query actually selects for display, the
          statement reconciliation resolver's own case verdict per booking, and -- side by side
          with the raw total_price -- what GET /reports/portfolio/dataset actually computes and
          renders on the Bookings tab for that same doc. A row where the two disagree ("&ne; raw
          total_price") is not what it looks like from total_price alone: it is FX-converting
          the stored currency label before display, which is exactly the out-of-window
          currency-mislabel-freeze bug this probe was extended to catch. Read-only, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Property
            </label>
            <Select value={propertyName} onValueChange={setPropertyName}>
              <SelectTrigger className="h-9 text-xs" data-testid="migration-probe-property-select">
                <SelectValue placeholder="Select property..." />
              </SelectTrigger>
              <SelectContent>
                {propsLoading && (
                  <SelectItem value="_loading" disabled>Loading...</SelectItem>
                )}
                {properties.map((p) => (
                  <SelectItem key={p.display_name} value={p.display_name}>
                    {p.display_name}
                    {p.has_listing_map_entry ? '' : ' (not migrated)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Window start
            </label>
            <Input
              value={start}
              onChange={(e) => setStart(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="font-mono"
            />
          </div>
          <div className="w-40">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Window end
            </label>
            <Input
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="font-mono"
            />
          </div>
          <Button onClick={run} disabled={loading || !canRun} data-testid="migration-probe-run">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <SearchCode className="h-4 w-4 mr-2" />
            )}
            Probe
          </Button>
        </div>

        {r && r.error && (
          <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-2">
            <p className="font-medium">{r.error}</p>
            {(r.candidates || []).length > 0 && (
              <div>
                <p className="text-xs font-medium">Candidates:</p>
                <ul className="text-xs list-disc list-inside">
                  {r.candidates.map((c, i) => (
                    <li key={i}>{c.display_name} (sm {c.smoobu_id || '—'} / hw {c.hostaway_id || '—'})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {r && !r.error && (
          <div className="space-y-3" data-testid="migration-probe-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>{r.property.display_name}</span>
              <span>|</span>
              <span className="font-mono">sm {r.property.smoobu_id || '—'} / hw {r.property.hostaway_id || '—'}</span>
              <span>|</span>
              <Badge variant={r.cutover.date ? 'outline' : 'secondary'} data-testid="migration-probe-cutover-badge">
                cutover: {r.cutover.date || 'not migrated'}
                {r.cutover.date ? ` (${r.cutover.source})` : ''}
              </Badge>
              <Badge variant="outline">{r.reservation_count} reservation(s)</Badge>
            </div>

            {reservations.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No reservations found in this window for this property.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2">Guest / dates</th>
                      <th className="text-left p-2">Pairing</th>
                      <th className="text-left p-2">Doc (source / id)</th>
                      <th className="text-right p-2">total_price</th>
                      <th className="text-right p-2">dataset endpoint returns</th>
                      <th className="text-center p-2">superseded_by</th>
                      <th className="text-left p-2">synced_at</th>
                      <th className="text-left p-2">Bookings tab / resolver verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ res, doc, ri, di }) => (
                      <tr
                        key={`${ri}-${di}`}
                        className={`border-t align-top ${di === 0 && ri > 0 ? 'border-t-2' : ''}`}
                        data-testid="migration-probe-row"
                      >
                        {di === 0 && (
                          <td className="p-2" rowSpan={res.docs.length}>
                            <div>{res.guest_name || <em>(empty)</em>}</div>
                            <div className="text-muted-foreground font-mono">
                              {res.check_in}&rarr;{res.check_out}
                            </div>
                          </td>
                        )}
                        {di === 0 && (
                          <td className="p-2" rowSpan={res.docs.length}>
                            <PairingBadge source={res.pairing_source} flag={res.pairing_flag} />
                          </td>
                        )}
                        <td className="p-2 font-mono">{doc.source} / {doc.id}</td>
                        <td className="p-2 text-right font-mono">
                          {doc.total_price ?? <span className="text-amber-600">—</span>}
                        </td>
                        <td className="p-2 text-right font-mono" data-testid="migration-probe-dataset-endpoint">
                          <DatasetEndpointCell doc={doc} />
                        </td>
                        <td className="p-2 text-center font-mono">{doc.superseded_by || '—'}</td>
                        <td className="p-2 font-mono">{doc.synced_at || '—'}</td>
                        <td className="p-2">
                          <CaseBadge verdict={res.statement_resolver_verdicts[doc.source]} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <CurrencyBackfillSection />
      </CardContent>
    </Card>
  );
}
